/**
 * AI Scribe — feature constants
 *
 * Everything the note stream branches on lives here; no meaningful literals in
 * the hook or the panels.
 *
 * @author [Rajkumar Rathod]
 */

/** Message types that are progress/metadata only — never a finished note. */
export const NOTE_STREAM_NON_DATA_TYPES = [
  "Completed",
  "IN_PROGRESS",
  "FAILED",
  "SIMPLE_SOAP_NOTE",
  "FALSE",
] as const;

export const NOTE_STREAM_STATUS = {
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "Completed",
  FAILED: "FAILED",
} as const;

/** Stream statuses after which the note data is still on its way. */
export const NOTE_STREAM_PENDING_STATUSES: readonly string[] = [
  NOTE_STREAM_STATUS.IN_PROGRESS,
  NOTE_STREAM_STATUS.COMPLETED,
];

/** Path appended to the API host for the transcription socket. */
export const NOTE_STREAM_SOCKET_PATH = "/api/master/transcriptions";

/** The socket goes quiet on some failures — stop showing a spinner forever. */
export const NOTE_STREAM_FALLBACK_TIMEOUT_MS = 300_000;

/** Keys the backend echoes back that must never be rendered as a note section. */
export const NOTE_METADATA_KEYS = [
  "patientId",
  "appointmentId",
  "transcriptionSegments",
] as const;

export const SCRIBE_DRAFT_TAB = {
  NOTES: "notes",
  TRANSCRIPTION: "transcription",
} as const;

export const SCRIBE_DRAFT_TABS = [
  { label: "Notes", value: SCRIBE_DRAFT_TAB.NOTES },
  { label: "Transcription", value: SCRIBE_DRAFT_TAB.TRANSCRIPTION },
];

export const SCRIBE_DRAFT_LABELS = {
  GENERATING: "Note generating...",
  FAILED: "Transcription & Note generation failed",
  RETRY: "Retry",
  NOTE_CARD_TITLE: "Note",
  NO_NOTE: "No note was generated for this session",
  NO_TRANSCRIPT: "No transcription available",
  PUSH_TO_EHR: "Export to Visit note Template",
  EMPTY_VALUE: "-",
} as const;

/** Only used when the visit note has no template selected yet. */
export const DEFAULT_SCRIBE_NOTE_TYPE = "SIMPLE_SOAP_NOTE";

/** localStorage prefix for the per-provider "Always apply for every note" instruction. */
export const SCRIBE_INSTRUCTIONS_STORAGE_PREFIX = "scribe-v2-instructions:";

/** Scribe statuses during which the provider may edit the instructions. */
export const SCRIBE_INSTRUCTIONS_EDITABLE_STATUSES = [
  "LISTENING",
  "PAUSED",
] as const;

export const SCRIBE_INSTRUCTIONS_LABELS = {
  TITLE: "Instructions to Scribe",
  PLACEHOLDER: "Add instructions",
  APPLY: "Apply instruction",
  CLEAR: "Clear instruction",
  ALWAYS_APPLY: "Always apply for every note",
  FINISH: "Finish & draft note",
} as const;

/** The scribe stores these vitals as bare converted numbers; the chart supplies the unit. */
export const SCRIBE_VITAL_UNITS: Readonly<Record<string, string>> = {
  weight: "lbs",
  height: "in",
  temperature: "°F",
};

export const BARE_NUMBER_PATTERN = /^\d+(\.\d+)?$/;
